import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';

import { RedirectRoute } from './redirect';

/**
 * Answers whether the current user may open the route it guards.
 *
 * This is a hook: implementations read MobX stores, route params and feature
 * flags. `RouteEntitlement` calls it unconditionally on every render, and route
 * definitions are static module-level values, so the identity never changes
 * between renders and the rules of hooks hold.
 */
export type UseIsRouteEnabled = () => boolean;

/**
 * Route-level entitlement gate.
 *
 * Before the routes moved into the data router, an unentitled route was simply
 * never registered, and the URL fell through to the `*` catch-all which rendered
 * `RedirectRoute`. The data router is created exactly once (recreating it leaks
 * history listeners — see appRouter), so routes can no longer be registered
 * conditionally on entitlement state. Instead every route is always registered
 * and redirects from the inside.
 *
 * The observable outcome is unchanged: the same destination and the same info
 * toast, both owned by `RedirectRoute`.
 *
 * Entitlements are always resolved by the time this renders — the app shell only
 * renders its <Outlet /> once `isStoresLoaded()` is true, which requires
 * `featuresStore.firstLoad` — so this does not flash a redirect while features
 * are still loading.
 */
/** Where an unentitled visitor is sent, when the section wants its own landing. */
type RouteEntitlementRedirect = { to?: string; message?: string };

const RouteEntitlement = observer(
  ({
    useIsEnabled,
    redirect,
    children,
  }: {
    useIsEnabled: UseIsRouteEnabled;
    redirect?: RouteEntitlementRedirect;
    children: ReactNode;
  }) => {
    const isEnabled = useIsEnabled();

    return isEnabled ? <>{children}</> : <RedirectRoute {...redirect} />;
  },
);

/**
 * Wraps a route element in its entitlement gate.
 *
 * Pass `redirect` where the section has its own landing page and wording — a
 * blocked page inside a section should return the visitor to that section, not
 * eject them to the app root with the generic message.
 */
export const entitled = (
  useIsEnabled: UseIsRouteEnabled,
  element: ReactNode,
  redirect?: RouteEntitlementRedirect,
): ReactNode => (
  <RouteEntitlement useIsEnabled={useIsEnabled} redirect={redirect}>
    {element}
  </RouteEntitlement>
);
